import React, { useState, useEffect, useRef } from 'react';
import { residentsAPI } from '../../../services/api';
import toast from 'react-hot-toast';

const AssignHouseholdModal = ({ show, onClose, resident, onAssigned }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
    if (!show) {
      setSearch('');
      setResults([]);
      setSelectedHousehold(null);
      setIsLoading(false);
      setIsAssigning(false);
    }
  }, [show]);

  useEffect(() => {
    if (search.length < 2) {
      setResults([]);
      return;
    }
    setIsLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await residentsAPI.getHouseholdHeads({ search });
        setResults(res.data.data || []);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const handleAssign = async () => {
    if (!selectedHousehold) {
      toast.error('Please select a household');
      return;
    }
    setIsAssigning(true);
    try {
      await residentsAPI.assignToHousehold(resident.id, selectedHousehold.id);
      toast.success('Resident reassigned to household');
      if (onAssigned) onAssigned();
    } catch (err) {
      toast.error('Failed to assign resident');
    } finally {
      setIsAssigning(false);
    }
  };

  if (!show || !resident) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <h2 className="text-lg font-bold mb-4">Reassign Resident to Household</h2>
        <div className="mb-2 text-sm text-gray-700">
          Resident: <span className="font-semibold">{resident.first_name} {resident.last_name}</span>
        </div>
        <div className="mb-4 relative">
          <input
            ref={inputRef}
            className="w-full border rounded px-3 py-2"
            placeholder="Search household head or number..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setSelectedHousehold(null);
            }}
            autoFocus
          />
          {isLoading && <div className="absolute right-3 top-3 text-xs text-gray-400">Searching...</div>}
          {search.length >= 2 && results.length > 0 && (
            <ul className="absolute z-10 left-0 right-0 bg-white border rounded shadow mt-1 max-h-40 overflow-y-auto">
              {results.map(hh => (
                <li
                  key={hh.id}
                  className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedHousehold?.id === hh.id ? 'bg-blue-100' : ''}`}
                  onClick={() => {
                    setSelectedHousehold(hh);
                    setSearch(`${hh.household_number} - ${hh.name} `);
                  }}
                >
                  <span className="font-medium">{hh.household_number}</span> - {hh.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 rounded"
            onClick={onClose}
            disabled={isAssigning}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-green-600 text-white rounded"
            onClick={handleAssign}
            disabled={isAssigning || !selectedHousehold}
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignHouseholdModal;
